/**
 * Supabase Configuration
 *
 * SETUP INSTRUCTIONS:
 * 1. Create a Supabase project at https://supabase.com
 * 2. Go to Project Settings → API
 * 3. Copy your Project URL and anon/public key
 * 4. Add to .env file:
 *    VITE_SUPABASE_URL=your_project_url
 *    VITE_SUPABASE_ANON_KEY=your_anon_key
 */

import { createClient } from '@supabase/supabase-js';
import { navigatorLock, NavigatorLockAcquireTimeoutError } from '@supabase/auth-js';

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

/**
 * Maximum time (ms) to wait for the auth Web Lock before giving up.
 * Supabase auth-js defaults to -1 (infinite) for most operations,
 * which can permanently hang if the lock is never released.
 */
const LOCK_ACQUIRE_MAX_TIMEOUT_MS = 5000;

/**
 * Custom lock that caps infinite timeouts to prevent permanent hangs.
 * Uses the Supabase navigatorLock implementation but replaces -1 (infinite)
 * with a bounded timeout. Falls back to running without a lock if the
 * Navigator Locks API is unavailable.
 */
async function boundedLock<R>(
  name: string,
  acquireTimeout: number,
  fn: () => Promise<R>
): Promise<R> {
  if (typeof globalThis.navigator?.locks?.request !== 'function') {
    return await fn();
  }
  const timeout = acquireTimeout < 0 ? LOCK_ACQUIRE_MAX_TIMEOUT_MS : acquireTimeout;
  try {
    return await navigatorLock(name, timeout, fn);
  } catch (e) {
    // Convert browser AbortError to Supabase's LockAcquireTimeoutError
    // so auto-refresh token tick silently retries instead of throwing
    if (e instanceof DOMException && e.name === 'AbortError') {
      throw new NavigatorLockAcquireTimeoutError(
        `Acquiring lock "${name}" timed out after ${timeout}ms`
      );
    }
    throw e;
  }
}

if (!supabaseUrl || !supabaseAnonKey) {
  console.warn(
    'Supabase credentials not found. Authentication features will be disabled.\n' +
      'To enable auth:\n' +
      '1. Create a Supabase project at https://supabase.com\n' +
      '2. Add VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY to your .env file'
  );
}

/**
 * Supabase client with persistent session configuration
 *
 * This ensures users stay logged in like Amazon/Google:
 * - persistSession: Stores session in localStorage
 * - autoRefreshToken: Refreshes tokens before they expire
 * - detectSessionInUrl: Handles OAuth callback redirects
 * - storageKey: Custom key to avoid conflicts with other apps
 */
export const supabase = createClient(
  supabaseUrl || 'https://placeholder.supabase.co',
  supabaseAnonKey || 'placeholder-anon-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      storageKey: 'engraving-pwa-auth',
      storage: typeof window !== 'undefined' ? window.localStorage : undefined,
      flowType: 'pkce',
      lock: boundedLock,
    },
  }
);

/**
 * Check if Supabase is properly configured
 */
export function isSupabaseConfigured(): boolean {
  return !!(
    supabaseUrl &&
    supabaseAnonKey &&
    supabaseUrl !== 'https://placeholder.supabase.co' &&
    supabaseAnonKey !== 'placeholder-anon-key'
  );
}
